/**
 * Card Click Analytics Utility
 *
 * Shared helper for generating card_click analytics configurations.
 * Reduces code duplication across different card types.
 *
 * @example ActionCard
 * ```tsx
 * const analyticsProps = analyticsConfig ? getCardClickConfig({
 *   analyticsConfig,
 *   cardType: 'action',
 *   cardTitle: heading || '',
 *   href,
 * }) : {};
 * ```
 *
 * @example DiscoverCard
 * ```tsx
 * const analyticsProps = analyticsConfig ? getCardClickConfig({
 *   analyticsConfig,
 *   cardType: 'discover',
 *   cardTitle: heading || body || '',
 *   href,
 * }) : {};
 * ```
 */

import type { CardClickEvent } from '../../composables/Analytics/types';
import { getClickConfig } from './getAnalyticsConfig';
import { ANALYTICS_EVENT_NAMES } from './constants';
import { getLinkInfo } from '../url';

/**
 * Configuration for card analytics
 * Extracted from component props
 */
export interface CardAnalyticsConfig {
  moduleName: string;
  componentFamily: string;
  componentType: string;
  sectionHeader?: string;
}

/**
 * Parameters for generating card click analytics
 */
export interface CardClickParams {
  /** Analytics configuration from parent component */
  analyticsConfig: CardAnalyticsConfig;
  /** Type of card ('action', 'discover', etc.) */
  cardType: string;
  /** Title or description of the card */
  cardTitle: string;
  /** Link URL */
  href: string;
  /** Whether the link is external */
}

/**
 * Generate card_click analytics configuration
 *
 * Standardizes card click tracking across all card components.
 * Automatically handles lowercase normalization and link type detection.
 *
 * @param params - Card analytics parameters
 * @returns Data attribute object for analytics tracking
 */
export const getCardClickConfig = ({
  analyticsConfig,
  cardType,
  cardTitle,
  href,
}: CardClickParams): { 'data-ga-config': string } | Record<string, never> => {
  const { normalizedHref, linkType } = getLinkInfo(href);
  return getClickConfig({
    event: ANALYTICS_EVENT_NAMES.CARD_CLICK,
    module_name: analyticsConfig.moduleName,
    component_family: analyticsConfig.componentFamily,
    component_type: analyticsConfig.componentType,
    section_header: analyticsConfig.sectionHeader?.toLowerCase(),
    card_type: cardType,
    card_title: cardTitle.toLowerCase(),
    link_url: normalizedHref,
    link_type: linkType,
  } as CardClickEvent);
};
